import type { PriceOverride } from './Analyzify.adapters'
import type { HeadlessMetafield } from 'analyzify-js'

/**
 * How the Analyzify SDK sources its configuration.
 *
 * - `off`   Provider is not mounted at all. No SDK code runs.
 * - `live`  Config is fetched from the Shopify shop metafield
 *           (`analyzify_app_metafield_export.headless_config`).
 * - `mock`  Config comes from MOCK_CONFIG below, for local development.
 *
 * `off` is the default because the production metafield is still empty (`{}`)
 * and the GTM container has no `ee_*` triggers yet - see SUP0000-60. Until both
 * land, mounting the provider would emit events nothing listens for.
 */
export const AnalyzifyMode = {
  OFF: 'off',
  LIVE: 'live',
  MOCK: 'mock',
} as const

export type AnalyzifyModeType = (typeof AnalyzifyMode)[keyof typeof AnalyzifyMode]

/**
 * Local-only stand-in for the headless config metafield.
 *
 * Deliberately enables *only* `gtm_dataLayer`, which is the flag the SDK checks
 * before pushing `ee_*` events onto window.dataLayer. Everything else stays off:
 *
 * - No `gtm_id`, so the SDK will not load a second GTM container. The container
 *   is loaded by <GoogleTagManager> in app/layout.tsx and stays that way.
 * - No `ga4_status`/`gads_status`/`meta_status`, so no vendor scripts load and
 *   no hits are sent. sendGA4Fallback() bails without `properties.GA4.status`,
 *   which keeps localhost traffic out of the client's real GA4 property.
 *
 * The result is that we can watch real `ee_*` payloads land on the dataLayer
 * without touching any live analytics account.
 */
export const MOCK_CONFIG: HeadlessMetafield = {
  'analyzify.gtm_dataLayer': { type: 'boolean', value: 'true' },
  'analyzify.gtm_variant_changed': { type: 'boolean', value: 'true' },
  'analyzify.gtm_forms': { type: 'boolean', value: 'true' },
  'analyzify.gtm_feed_country': { type: 'single_line_text_field', value: 'AU' },
  'analyzify.gtm_feed_language': { type: 'single_line_text_field', value: 'en' },
  'analyzify.gads_feed_country': { type: 'single_line_text_field', value: 'AU' },
  'analyzify.gads_feed_region': { type: 'single_line_text_field', value: 'AU' },
  'analyzify.currency': { type: 'single_line_text_field', value: 'AUD' },
}

/**
 * Whether the SDK is mounted at all.
 *
 * Every tracker must check this before rendering anything that calls
 * useAnalyzify() - that hook throws outside an AnalyzifyProvider, and in `off`
 * mode the provider is not mounted.
 */
export const isAnalyzifyEnabled = (mode: AnalyzifyModeType) => mode !== AnalyzifyMode.OFF

/**
 * Price reported on view-stage events: view_item, view_item_list, select_item.
 *
 * The site does not display a price anywhere at view time. Enquiry products have
 * no price at all, LPG resolves its price from the BFF only after a postcode is
 * entered, and the Heater price is revealed only once the onboarding form is
 * completed. The Shopify catalogue price behind these products is a placeholder
 * the customer is never shown, so reporting it would put numbers into GA4 that
 * exist nowhere in the UI.
 *
 * Everything from add_to_cart onwards carries the real price, because those read
 * the Cart Transform value off the cart line.
 */
export const VIEW_STAGE_PRICE: PriceOverride = { amount: 0 }
